import React, { useEffect } from "react";
import parse from "html-react-parser";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { API_BASE_URL } from "src/_config";

interface FormData {
  candidateName: string;
  jobTitle: string;
  department: string;
  companyName: string;
  startDate: string;
  salary: string;
  currencyType: string;
  employmentType: string;
  workplace: string;
  workingHours: string;
  reportingTo: string;
  benefits: string;
  includeDigitalSignature: boolean;
}

interface OfferLetterPreviewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  formData: FormData;
  candidateId: number;
  onSendToCandidate: () => void;
  offerLetterContent: string;
  setOfferLetterContent: (content: string) => void;
  candidateData?: any;
}

export default function OfferLetterPreviewPanel({
  isOpen,
  onClose,
  formData,
  candidateId,
  onSendToCandidate,
  offerLetterContent,
  setOfferLetterContent,
  candidateData,
}: OfferLetterPreviewPanelProps) {
  const { t } = useTranslation("job-details");
  const userDetails = useAppUserDetailsSelector();

  // Generate HTML preview based on form data
  const generateOfferLetterHTML = () => {
    const companyEmail = userDetails?.company?.recruiter_email;
    const companyPhone = userDetails?.company?.recruiter_mobile;
    const companyWebsite = userDetails?.company?.website || "";
    const recruiterName = userDetails?.company?.recruiter_name || "Hiring Manager";
    const recruiterDesignation = userDetails?.company?.recruiter_designation || "Human Resource Manager";

    // Get candidate details from fullCandidateData
    console.log("candidateData in preview:", candidateData);
    console.log("fullCandidateData:", candidateData?.fullCandidateData);

    // Access jobseeker_details from fullCandidateData
    const jobseekerDetails = candidateData?.fullCandidateData?.jobseeker_details?.[0];
    const basicDetails = jobseekerDetails?.basic_details?.[0];

    const candidateEmail = basicDetails?.email || "";
    const candidateAddress = basicDetails?.address || "";
    const candidateLocation =
      basicDetails?.city && basicDetails?.country
        ? `${basicDetails.city}, ${basicDetails.country}`
        : basicDetails?.city || basicDetails?.country || "";

    // Get company logo URL or use DrJob logo as fallback
    const companyLogoUrl = userDetails?.company?.logo_url
      ? userDetails.company.logo_url
      : window.location.origin + "/images/dr-logo.png";

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; background: #fff;">
        <!-- Header with blue gradient (DrJob theme) -->
        <div style="position: relative; background: #a5c5e5; height: 120px; margin: -40px -40px 30px -40px; padding: 20px 40px; display: flex; justify-content: space-between; align-items: center; border-radius: 8px;">
          <div style="font-size: 14px;">
            <div style="font-weight: 600; margin-bottom: 4px;">${formData.companyName || "Company Name"}</div>
            <div>${companyEmail} </div>
            <div> ${companyWebsite}</div>
            <div> ${companyPhone}</div>
          </div>
          <div style="background: #fff; width: 80px; height: 60px; display: flex; align-items: center; justify-content: center; border-radius: 4px; padding: 8px;">
            <img src="${companyLogoUrl}" alt="Company Logo" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
          </div>
        </div>

        <!-- Title -->
        <h1 style="text-align: center; font-size: 28px; font-weight: bold; color: #333; margin-bottom: 30px; text-transform: uppercase; letter-spacing: 2px;">
          JOB OFFER LETTER
        </h1>

        <!-- Date and Candidate Info -->
        <div style="margin-bottom: 20px; color: #666; line-height: 1.8;">
          <div style="margin-bottom: 10px;">${new Date().toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}</div>
          <div style="font-weight: 600; color: #333;">${formData.candidateName || "Candidate Name"}</div>
          ${candidateAddress ? `<div>${candidateAddress}</div>` : ""}
          ${candidateLocation ? `<div>${candidateLocation}</div>` : ""}
          <div>${candidateEmail}</div>
        </div>

        <!-- Salutation -->
        <div style="margin-bottom: 20px; color: #333; line-height: 1.8;">
          <strong>Dear ${formData.candidateName || "Candidate Name"},</strong>
        </div>

        <!-- Offer Paragraph -->
        <div style="margin-bottom: 30px; color: #333; line-height: 1.8;">
          We are pleased to offer you the position of <strong>${formData.jobTitle || "Job Title"}</strong> at <strong>${
      formData.companyName || "Company Name"
    }</strong>. We believe your skills will greatly contribute to our team.
        </div>

        <!-- Details Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px; background: #f9f9f9;">
          <thead>
            <tr style="background: #e8e8e8;">
              <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: 600; color: #666;">Detail</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: 600; color: #666;">Information</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Job Title</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.jobTitle || "N/A"}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Department</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.department || "N/A"}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Start Date</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.startDate || "N/A"}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Employment Type</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.employmentType || "N/A"}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Workplace</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.workplace || "N/A"}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Salary</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.currencyType || "£"}${
      formData.salary || "N/A"
    }</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Working Hours Per Week</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.workingHours || "N/A"}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border: 1px solid #ddd; color: #333;">Reporting To</td>
              <td style="padding: 12px; border: 1px solid #ddd; color: #666;">${formData.reportingTo || "N/A"}</td>
            </tr>
          </tbody>
        </table>

        <!-- Terms and Conditions -->
        <div style="margin-bottom: 20px; color: #666; line-height: 1.8; font-size: 14px;">
          This offer is contingent upon the successful completion of reference checks. Your employment will follow our standard terms and conditions, which we will provide upon acceptance of this offer.
        </div>

        ${
          formData.benefits
            ? `
        <div style="margin-bottom: 20px; color: #666; line-height: 1.8; font-size: 14px;">
          <strong style="color: #333;">Benefits:</strong><br/>
          ${formData.benefits}
        </div>
        `
            : ""
        }

        <!-- Response Deadline -->
        <div style="margin-bottom: 20px; color: #666; line-height: 1.8; font-size: 14px;">
          Please confirm your acceptance by signing and returning a copy of this letter by <strong>${new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000
          ).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}</strong>. We are excited about the possibility of you joining the company and look forward to your positive response. If you have any questions, please contact me at <strong>${companyEmail}</strong> or <strong>${companyPhone}</strong>.
        </div>

        <!-- Closing -->
        <div style="margin-bottom: 40px; color: #333; line-height: 1.8;">
          <div style="margin-bottom: 10px;">Sincerely,</div>
          <div style="font-weight: 600; margin-bottom: 5px;">${recruiterName}</div>
          <div style="color: #666; font-size: 14px;">${recruiterDesignation}</div>
        </div>

        
      </div>
    `;
  };

  // Generate and store HTML content when panel opens
  useEffect(() => {
    if (isOpen && formData && !offerLetterContent) {
      const htmlContent = generateOfferLetterHTML();
      setOfferLetterContent(htmlContent);
    }
  }, [isOpen, formData]);

  if (!isOpen) {
    return null;
  }

  const htmlContent = offerLetterContent || generateOfferLetterHTML();

  // Format text to handle line breaks
  const formatOfferLetterContent = (content: string) => {
    if (!content) return "";
    return parse(content);
  };

  const handleSendToCandidate = () => {
    // Pass the HTML content to the parent component
    onSendToCandidate();
  };

  return (
    <div className="offer-preview-panel-overlay" onClick={onClose}>
      <div className="offer-preview-panel" onClick={(e) => e.stopPropagation()}>
        <div className="offer-preview-header">
          <h3>{t("jobDetails.finalDecision.previewPanel.title")}</h3>
          <p>
            {t("jobDetails.finalDecision.previewPanel.for")} {formData.jobTitle}
          </p>
          <button className="close-panel" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="offer-preview-content">
          <div className="offer-details">{formatOfferLetterContent(htmlContent)}</div>
          {formData.includeDigitalSignature ? (
            <div
              style={{
                textAlign: "center",
                color: "#666",
                fontSize: "14px",
                padding: "20px",
                background: "#f0f9ff",
                borderRadius: "4px",
                border: "1px solid #bfdbfe",
              }}
            >
              <p>
                <i className="fa fa-info-circle"></i> Digital signature will be required upon acceptance
              </p>
            </div>
          ) : null}
        </div>

        <div className="offer-preview-send">
          <button className="btn-preview" onClick={handleSendToCandidate}>
            <i className="fa fa-paper-plane"></i>
            {t("jobDetails.finalDecision.previewPanel.sendToCandidate")}
          </button>
        </div>
      </div>
    </div>
  );
}
